import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Iter-311b — Public Terms & Conditions.
 *
 * Route: /terms-and-conditions
 *
 * Contains the SMS Messaging Terms section required by Twilio A2P
 * registration (Program Name, message types, frequency, rates, STOP/HELP,
 * support contact, Privacy Policy link).
 */
const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 px-4 py-12">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="terms-and-conditions-page">
        <div>
          <h1 className="text-3xl font-bold text-white">iEvntür Terms &amp; Conditions</h1>
          <p className="text-zinc-400 text-sm mt-1">Effective 2026-07-01 · v1</p>
        </div>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">1. Acceptance</h2>
          <p>
            By creating an account or using the iEvntür platform, you agree
            to these Terms &amp; Conditions and to our{' '}
            <Link to="/privacy-policy" className="text-amber-400 underline">Privacy Policy</Link>.
            If you do not agree, do not use the platform.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">2. Eligibility</h2>
          <p>
            You must be at least 18 years old to use iEvntür. Talent must
            complete identity verification before accepting paid gigs.
            Clients must be authorized to act on behalf of their organization.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">3. Accounts</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>You are responsible for your credentials and account activity.</li>
            <li>Duplicate accounts, account sharing, or misrepresentation are prohibited.</li>
            <li>We may suspend or terminate accounts that violate these terms.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">4. Marketplace Rules</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Clients set bill rates within platform pricing bands (or negotiated locked rates).</li>
            <li>Talent commit to shifts they accept. No-shows and last-minute cancellations may affect your standing.</li>
            <li>Ratings must be honest and reflect the actual experience.</li>
            <li>Direct hire outside the platform during or shortly after an engagement is subject to the Direct Hire fee described in your client contract.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">5. Payments</h2>
          <p>
            Talent are paid based on approved hours × the agreed rate.
            Clients are billed on a per-event consolidated invoice (one
            invoice per event with itemized staff, roles, hours, rates, and
            extended amounts) unless an alternative billing preference has
            been agreed upon (weekly, monthly, or cost-code split).
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">6. Prohibited Uses</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Fraudulent bookings, false ratings, harassment, discrimination.</li>
            <li>Attempts to bypass identity verification or GPS attendance checks.</li>
            <li>Automated scraping or resale of platform data.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">7. Disclaimers &amp; Limitation of Liability</h2>
          <p>
            The platform is provided &quot;as is&quot; without warranties of any kind.
            To the maximum extent permitted by law, iEvntür&apos;s aggregate
            liability arising out of or related to these Terms is limited to
            the total fees paid or payable to iEvntür in the twelve months
            preceding the claim.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">8. Governing Law</h2>
          <p>
            These terms are governed by the laws of the State of Georgia,
            USA. Disputes shall be resolved by binding arbitration in
            Atlanta, GA, unless otherwise required by law.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-xl font-semibold text-white">9. Changes</h2>
          <p>
            We may update these Terms from time to time. Material changes
            take effect 14 days after posting; continued use of the
            platform constitutes acceptance.
          </p>
        </section>

        {/* ────────────── SMS MESSAGING TERMS (Twilio A2P) ────────────── */}
        <section
          className="space-y-3 border-t border-zinc-800 pt-6"
          data-testid="sms-messaging-terms-section"
        >
          <h2 className="text-2xl font-bold text-white">10. SMS Messaging Terms</h2>

          <div>
            <h3 className="text-lg font-semibold text-white">Program Name</h3>
            <p><strong className="text-zinc-100">iEvntür SMS Notifications</strong></p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Types of Messages</h3>
            <p>
              When you opt in, you may receive SMS messages related to:
            </p>
            <ul className="list-disc pl-6">
              <li>Account verification codes</li>
              <li>Booking confirmations</li>
              <li>Shift reminders and schedule changes</li>
              <li>Payment / payout notifications</li>
              <li>Job opportunities and invitations</li>
              <li>Ratings and post-event follow-ups</li>
              <li>Account and security notifications</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Message Frequency</h3>
            <p>
              Message frequency varies based on your activity. Active talent
              and clients typically receive 1–10 messages per booking;
              inactive accounts receive 0–2 messages per month.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Message and Data Rates</h3>
            <p>
              Message and data rates may apply. iEvntür does not charge for
              SMS messages; standard carrier fees from your wireless provider
              may apply.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Opt-Out</h3>
            <p>
              Reply <strong>STOP</strong> to any iEvntür SMS message to
              unsubscribe. You will receive a confirmation that you have been
              opted out, and you will no longer receive SMS from us aside
              from critical security alerts you cannot disable.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Help</h3>
            <p>
              Reply <strong>HELP</strong> to any iEvntür SMS message to receive
              our support contact information.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Support</h3>
            <p>
              Email:{' '}
              <a href="mailto:support@ievntur.com" className="text-amber-400 underline">
                support@ievntur.com
              </a>
            </p>
            <p>
              Website:{' '}
              <a href="https://app.ievntur.com" className="text-amber-400 underline">
                https://app.ievntur.com
              </a>
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Consent</h3>
            <p>
              SMS consent is voluntary and is <strong>NOT</strong> a condition of
              purchasing or using the iEvntür platform. Consent is captured
              via a separate checkbox during registration (unchecked by
              default) and can be revoked at any time.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Privacy</h3>
            <p>
              We never share or sell your mobile phone number, opt-in status,
              or SMS message content with third parties for their own
              promotional or marketing purposes. See our{' '}
              <Link to="/privacy-policy" className="text-amber-400 underline" data-testid="sms-link-privacy">
                Privacy Policy
              </Link>{' '}
              for full data-handling terms.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-white">Carrier Disclaimer</h3>
            <p>
              Carriers are not liable for delayed or undelivered messages.
              Supported carriers include AT&amp;T, Verizon, T-Mobile, Sprint,
              and most regional and prepaid carriers.
            </p>
          </div>
        </section>

        <p className="text-xs text-zinc-500 border-t border-zinc-800 pt-4">
          Questions?{' '}
          <a href="mailto:support@ievntur.com" className="text-amber-400 underline">
            support@ievntur.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
